/* eslint-disable max-lines */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppContext, ToastrContext } from '../../contexts';
import { Loader } from '@mantine/core';
import EditTermCondition from '../../components/forms/EditTermCondition/EditTermCondition';
import {
  editSubPangkalanTermCondition,
  fetchTermConditionById,
  fetchTermConditionHistory,
} from './actions';
import { uploadImageFaq } from '../../utils/api';
import BreadcrumbLink from '../../components/elements/BreadcrumbLink/BreadcrumbLink';
import { userTypeReverseMap } from './constants';

export default function TermConditionUpdate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const agreementId = searchParams.get('agreementId');
  const historyId = searchParams.get('historyId');
  const tabName = searchParams.get('userType');

  const { setBreadcrumbs } = useContext(AppContext);
  const { setToastr } = useContext(ToastrContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [errorUpload, setErrorUpload] = useState('');
  const [initialData, setInitialData] = useState(null);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [contentMetadata, setContentMetadata] = useState({
    userType: null,
    contentType: null,
  });

  const {
    isLoadingUpdateSubPangkalanTermCondition,
    messageUpdateSubPangkalanTermCondition,
    dataTermConditionHistory,
    isLoadingTermConditionHistory,
  } = useSelector((s) => ({
    isLoadingUpdateSubPangkalanTermCondition:
      s.termCondition.isLoadingUpdateSubPangkalanTermCondition,
    messageUpdateSubPangkalanTermCondition:
      s.termCondition.messageUpdateSubPangkalanTermCondition,
    dataTermConditionHistory: s.termCondition.dataTermConditionHistory || {
      data: [],
      meta: {},
    },
    isLoadingTermConditionHistory:
      s.termCondition.isLoadingTermConditionHistory,
  }));

  const versionHistory = dataTermConditionHistory?.data || [];
  const historyMeta = dataTermConditionHistory?.meta || {};
  const hasMoreHistory = (historyMeta.page || 0) < (historyMeta.totalPage || 0);

  const fetchData = useCallback(async () => {
    try {
      setIsLoadingData(true);

      if (historyId) {
        const responseData = await dispatch(fetchTermConditionById(historyId));

        if (responseData) {
          setInitialData(responseData);
          setSelectedVersionId(historyId);

          const metadata = {
            userType: responseData.userType,
            contentType: responseData.contentType,
          };
          setContentMetadata(metadata);

          const userType = responseData.userType;
          const tabName =
            userType === 1 || userType === '1' ? 'Pangkalan' : 'Subpangkalan';
          const isPangkalan = userType === 1 || userType === '1';

          const handleTabClick = () => {
            const searchParam = isPangkalan ? '' : '?tab=subpangkalan';
            navigate(`/term-condition${searchParam}`);
          };

          setBreadcrumbs([
            'Manajemen Kebijakan',
            <BreadcrumbLink
              key="tab-link"
              onClick={handleTabClick}
              text={tabName}
            />,
            `Ubah Kebijakan`,
          ]);

          if (metadata.userType !== null && metadata.contentType !== null) {
            try {
              const historyParams = {
                page: 1,
                userType: metadata.userType|| 1,
                contentType: metadata.contentType
              };

              await dispatch(fetchTermConditionHistory(historyParams));
              setHistoryPage(1);
            } catch (historyError) {
              setIsLoadingData(false);
            }
          }
        }
      } else {
        setBreadcrumbs(['Manajemen Kebijakan', 'Ubah Syarat & Ketentuan']);
      }
    } catch (error) {
      setIsLoadingData(false);
    } finally {
      setIsLoadingData(false);
    }
  }, [historyId, setBreadcrumbs, dispatch, history]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (isSubmitting && !isLoadingUpdateSubPangkalanTermCondition) {
      const category = initialData?.category || 'S&K';

      if (messageUpdateSubPangkalanTermCondition === '') {
        setToastr({
          desc: `${category} berhasil diubah`,
          type: 'success',
          visible: true,
        });

        const searchParams = new URLSearchParams();
        const userType = contentMetadata.userType || initialData?.userType;
        const targetTab = tabName
          ? tabName.toLowerCase()
          : (userTypeReverseMap[userType] || 'pangkalan');

        if (targetTab) {
          searchParams.set('tab', targetTab);
        }

        navigate({
          pathname: '/term-condition',
          search: searchParams.toString(),
        });
      } else {
        setToastr({
          desc: `${category} gagal diubah`,
          type: 'danger',
          visible: true,
        });
      }
      setIsSubmitting(false);
    }
  }, [
    isLoadingUpdateSubPangkalanTermCondition,
    messageUpdateSubPangkalanTermCondition,
    initialData,
    contentMetadata,
    history,
    setToastr,
    isSubmitting,
  ]);

  const handleLoadMore = async () => {
    if (!hasMoreHistory || isLoadingTermConditionHistory) return;

    try {
      const page = historyPage + 1;

      const params = {
        page,
        userType: contentMetadata.userType,
        contentType: contentMetadata.contentType,
      };

      await dispatch(fetchTermConditionHistory(params));
      setHistoryPage(page);
    } catch (error) {
      // Silently handle error to avoid UI disruption
    }
  };

  const handleHistoryItemClick = async (id) => {
    if (id === selectedVersionId) return;

    setIsLoadingData(true);
    try {
      const responseData = await dispatch(fetchTermConditionById(id));

      if (responseData) {
        setInitialData(responseData);
        setSelectedVersionId(id);

        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('historyId', id);
        navigate({
          pathname: location.pathname,
          search: newSearchParams.toString(),
        });
      }
    } catch (error) {
      // console.error('Error fetching history item details:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleBackClick = () => {
    const userType = contentMetadata.userType || initialData?.userType;
    const isPangkalan = userType === 1 || userType === '1';

    const searchParams = new URLSearchParams();
    if (!isPangkalan) {
      searchParams.set('tab', userTypeReverseMap[userType] || 'pangkalan');
    }

    navigate({
      pathname: '/term-condition',
      search: searchParams.toString(),
    });
  };

  const checkVersionExists = (version) => {
    return versionHistory.some(
      (item) => item.version === version && item.id !== initialData?.id
    );
  };

  const fileToBase64 = (val) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(val);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const uploadImageCallBack = async (blobInfo, success) => {
    const file = blobInfo.blob();
    setErrorUpload('');
    if (file.size <= 2000000) {
      try {
        const base64 = await fileToBase64(file);
        const params = {
          image: base64,
        };
        const data = await uploadImageFaq(params);
        return success(data?.data?.link);
      } catch (error) {
        return error;
      }
    } else {
      setErrorUpload('Maksimal Ukuran file 2 MB');
    }
  };

  const handleSubmit = (values) => {
    if (isSubmitting || isLoadingUpdateSubPangkalanTermCondition) return;

    if (checkVersionExists(values.version)) {
      setToastr({
        desc: 'Versi tidak boleh sama dengan versi sebelumnya',
        type: 'danger',
        visible: true,
      });
      return;
    }

    const { title, content, version, isEnabled, isRequired } = values;
    const submitParams = {
      title,
      content,
      version,
      isEnabled: isEnabled === 'true',
      isRequired: isRequired === 'true',
    };

    setIsSubmitting(true);

    if (!agreementId) {
      setToastr({
        desc: 'Terjadi kesalahan: ID tidak ditemukan',
        type: 'danger',
        visible: true,
      });
      setIsSubmitting(false);
      return;
    }

    dispatch(editSubPangkalanTermCondition(agreementId, submitParams));
  };

  if (isLoadingData) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px',
        }}
      >
        <Loader size={40} />
      </div>
    );
  }

  return (
    <EditTermCondition
      error={errorUpload}
      errorUpload={errorUpload}
      hasMoreHistory={hasMoreHistory}
      initialData={initialData}
      isLoadingHistory={isLoadingTermConditionHistory}
      onBackClick={handleBackClick}
      onHistoryItemClick={handleHistoryItemClick}
      onLoadMore={handleLoadMore}
      onSubmit={handleSubmit}
      selectedVersionId={selectedVersionId}
      uploadImageCallBack={uploadImageCallBack}
      versionHistory={versionHistory}
    />
  );
}
